import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { apiError } from "@/lib/errors";
import { toast } from "sonner";

/** PermissionMatrix — grouped Allow/Deny table used by User Access + Role default_permissions.
 *  Props:
 *    catalog          — { [category]: [{key, label, root_only?}, ...] } from /api/admin/permissions/catalog
 *    rootOnlyKeys     — array/Set of permission keys flagged root_only in the registry
 *    roleDefaults     — { perm_key: "allow" }  (baseline)
 *    userOverrides    — { perm_key: "allow"|"deny" } (may be null in role-detail mode)
 *    mode             — "role" | "user"
 *    onChangeDefaults — for mode="role": (nextDefaults) => void
 *    onChangeOverrides — for mode="user":  (nextOverrides) => void
 *    superAdminLocked — true = disable the root_only sentinel keys
 *    adminAutoInherit — true when editing an Admin-tier role; renders
 *                       every non-root permission as auto-granted (locked,
 *                       inherited) and hides the manual toggle.
 */
export function PermissionMatrix({ catalog, rootOnlyKeys = [], roleDefaults = {}, userOverrides = {}, mode,
                                    onChangeDefaults, onChangeOverrides, superAdminLocked,
                                    adminAutoInherit = false }) {
  const rootSet = React.useMemo(() => new Set(rootOnlyKeys || []), [rootOnlyKeys]);
  const isSentinel = (k) => rootSet.has(k) || Boolean(catalog && Object.values(catalog).flat().find((r) => r.key === k && r.root_only));
  const roleAllows = (k) => roleDefaults[k] === "allow";
  const userSelect = (k) => userOverrides?.[k] || "inherit";
  const effective = (k) => {
    const o = userSelect(k);
    if (o === "allow") return true;
    if (o === "deny") return false;
    return roleAllows(k);
  };
  const setDefault = (k, allow) => {
    const next = { ...roleDefaults };
    if (allow) next[k] = "allow"; else delete next[k];
    onChangeDefaults(next);
  };
  const setOverride = (k, v) => {
    const next = { ...(userOverrides || {}) };
    if (v === "inherit") delete next[k]; else next[k] = v;
    onChangeOverrides(next);
  };
  return (
    <div className="space-y-4" data-testid="permission-matrix">
      {adminAutoInherit && (
        <div className="rounded-md border border-coach-blue/40 bg-coach-blue/5 px-3 py-2 text-[12px] text-carbon" data-testid="admin-auto-inherit-notice">
          <span className="font-medium text-deep-road-navy">Admin auto-inheritance:</span> every non-root permission below is granted automatically at runtime. Root/Super Admin-only permissions (labelled below) are always denied to Admin.
        </div>
      )}
      {Object.entries(catalog || {}).map(([cat, rows]) => (
        <div key={cat} className="bg-white border border-coach-silver rounded-md">
          <div className="px-3 py-2 border-b border-coach-silver text-[11px] uppercase tracking-widest text-steel font-medium bg-road-white">
            {cat}
          </div>
          <table className="w-full text-[12.5px]">
            <thead className="text-[10px] uppercase tracking-widest text-steel">
              <tr>
                <th className="text-left px-3 py-1.5">Permission</th>
                {mode === "user" && <th className="text-center px-2 py-1.5 w-24">Role Default</th>}
                <th className="text-center px-2 py-1.5 w-40">
                  {mode === "user" ? "User Override" : "Setting"}
                </th>
                {mode === "user" && <th className="text-center px-2 py-1.5 w-20">Effective</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const rootOnly = rootSet.has(r.key) || r.root_only;
                const locked = superAdminLocked && isSentinel(r.key);
                const adminInherited = adminAutoInherit && !rootOnly;
                return (
                  <tr key={r.key} className="border-t border-coach-silver/60"
                      data-testid={`perm-row-${r.key}`}>
                    <td className="px-3 py-1.5">
                      <div className="text-carbon flex items-center gap-2">
                        {r.label}
                        {rootOnly && <span className="text-[9.5px] uppercase tracking-widest border border-touring-lime text-carbon rounded-sm px-1 py-0.5">Super Admin only</span>}
                      </div>
                      <div className="text-[10.5px] text-steel font-mono">{r.key}</div>
                    </td>
                    {mode === "user" && (
                      <td className="text-center">
                        {roleAllows(r.key)
                          ? <span className="inline-block px-1.5 py-0.5 text-[10px] rounded-sm bg-coach-blue/10 text-coach-blue">Allow</span>
                          : <span className="inline-block px-1.5 py-0.5 text-[10px] rounded-sm bg-chrome-mist text-steel">Deny</span>}
                      </td>
                    )}
                    <td className="text-center">
                      {adminInherited ? (
                        <span className="inline-flex items-center gap-1 text-[11px] text-coach-blue" data-testid={`perm-inherited-${r.key}`}>
                          <span className="w-1.5 h-1.5 rounded-full bg-coach-blue" /> Inherited (Admin)
                        </span>
                      ) : mode === "user" ? (
                        <select
                          value={userSelect(r.key)}
                          onChange={(e) => setOverride(r.key, e.target.value)}
                          disabled={locked}
                          data-testid={`perm-override-${r.key}`}
                          className="border border-coach-silver rounded-sm text-[11px] px-2 py-1 bg-white text-carbon disabled:opacity-50"
                        >
                          <option value="inherit">Inherit</option>
                          <option value="allow">Allow</option>
                          <option value="deny">Deny</option>
                        </select>
                      ) : (
                        <label className="inline-flex items-center gap-2 text-[11.5px] text-carbon">
                          <input
                            type="checkbox"
                            checked={roleAllows(r.key)}
                            disabled={locked}
                            onChange={(e) => setDefault(r.key, e.target.checked)}
                            data-testid={`perm-default-${r.key}`}
                          />
                          {roleAllows(r.key) ? "Allow" : "Deny"}
                        </label>
                      )}
                    </td>
                    {mode === "user" && (
                      <td className="text-center">
                        {effective(r.key)
                          ? <span className="text-coach-blue font-medium">✓ Allow</span>
                          : <span className="text-steel">✗ Deny</span>}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

/** WidgetPicker — Available (left) + Selected (right, ordered).
 * Props:
 *   catalog    — WIDGET_CATALOG array
 *   selected   — array of widget keys, ordered
 *   onChange   — (nextArray) => void
 *   allowedPerms — Set of perm keys the target has (used to gray widgets they can't see)
 */
export function WidgetPicker({ catalog, selected = [], onChange, allowedPerms }) {
  const sel = selected || [];
  const selectedSet = new Set(sel);
  const available = catalog.filter((w) => !selectedSet.has(w.key));
  const groups = {};
  available.forEach((w) => { (groups[w.category] ||= []).push(w); });

  const add = (k) => onChange([...sel, k]);
  const remove = (k) => onChange(sel.filter((x) => x !== k));
  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= sel.length) return;
    const next = [...sel];
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange(next);
  };
  const has = (perm) => !allowedPerms || allowedPerms.has(perm);

  return (
    <div className="grid md:grid-cols-2 gap-4" data-testid="widget-picker">
      <div className="bg-white border border-coach-silver rounded-md">
        <div className="px-3 py-2 border-b border-coach-silver text-[11px] uppercase tracking-widest text-steel font-medium bg-road-white">
          Available Widgets
        </div>
        <div className="max-h-[500px] overflow-y-auto">
          {Object.entries(groups).map(([cat, ws]) => (
            <div key={cat}>
              <div className="px-3 py-1 text-[10px] uppercase tracking-widest text-steel bg-road-white/50">{cat}</div>
              {ws.map((w) => (
                <div key={w.key} className="flex items-center justify-between px-3 py-1.5 border-b border-coach-silver/60 text-[12.5px]">
                  <div className="min-w-0">
                    <div className={has(w.required_permission) ? "text-carbon" : "text-steel line-through"}>{w.label}</div>
                    <div className="text-[10.5px] text-steel font-mono">{w.key}
                      {w.is_future_placeholder && <span className="ml-2 text-amber-hold">Future Shop</span>}
                    </div>
                  </div>
                  <button type="button" onClick={() => add(w.key)}
                          data-testid={`widget-add-${w.key}`}
                          className="text-[11px] px-2 py-0.5 rounded-sm border border-coach-blue text-coach-blue hover:bg-coach-blue hover:text-white">+ Add</button>
                </div>
              ))}
            </div>
          ))}
          {available.length === 0 && <div className="p-3 text-[12px] text-steel italic">All widgets selected.</div>}
        </div>
      </div>
      <div className="bg-white border border-coach-silver rounded-md">
        <div className="px-3 py-2 border-b border-coach-silver text-[11px] uppercase tracking-widest text-steel font-medium bg-road-white flex items-center justify-between">
          <span>Selected Widgets (order matters)</span>
          <span className="text-steel">{sel.length}</span>
        </div>
        <div className="max-h-[500px] overflow-y-auto">
          {sel.length === 0 && <div className="p-3 text-[12px] text-steel italic">No widgets selected. Add widgets from the left.</div>}
          {sel.map((k, idx) => {
            const w = catalog.find((c) => c.key === k);
            if (!w) return null;
            return (
              <div key={k} className="flex items-center justify-between px-3 py-1.5 border-b border-coach-silver/60 text-[12.5px]"
                   data-testid={`widget-selected-${k}`}>
                <div className="min-w-0">
                  <div className="text-carbon">{idx + 1}. {w.label}</div>
                  <div className="text-[10.5px] text-steel font-mono">{w.category} · {w.key}</div>
                </div>
                <div className="flex items-center gap-1">
                  <button type="button" onClick={() => move(idx, -1)} disabled={idx === 0}
                          className="text-[11px] px-1.5 border border-coach-silver rounded-sm disabled:opacity-40">↑</button>
                  <button type="button" onClick={() => move(idx, +1)} disabled={idx === sel.length - 1}
                          className="text-[11px] px-1.5 border border-coach-silver rounded-sm disabled:opacity-40">↓</button>
                  <button type="button" onClick={() => remove(k)}
                          data-testid={`widget-remove-${k}`}
                          className="text-[11px] px-2 border border-critical-red text-critical-red hover:bg-critical-red hover:text-white rounded-sm">Remove</button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/** useAdminData — small hook to load catalogs + roles once. */
export function useAdminData() {
  const [state, setState] = useState({ loading: true, permCatalog: null, rootOnlyKeys: [], widgets: [], roles: [], me: null });
  useEffect(() => {
    (async () => {
      try {
        const [pc, w, rl, me] = await Promise.all([
          api.get("/dashboard/permissions/catalog").then((r) => r.data),
          api.get("/dashboard/widgets").then((r) => r.data.widgets),
          api.get("/admin/roles").then((r) => r.data.results),
          api.get("/dashboard/permissions/me").then((r) => r.data),
        ]);
        setState({
          loading: false,
          permCatalog: pc?.catalog || null,
          rootOnlyKeys: pc?.root_only_keys || [],
          widgets: w,
          roles: rl,
          me,
        });
      } catch (e) {
        toast.error(apiError(e));
        setState((s) => ({ ...s, loading: false }));
      }
    })();
  }, []);
  return state;
}
